import React, { useEffect, useState, useCallback } from "react";
import { colors } from '../../../theme/colors';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Divider,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  FormControl,
  Select,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import { axiosInstance } from "../../../utils/axiosInstance";
import { FinalShortlistedEndPoint } from "../../../api-routes";
import { FinalShortlistedWrapper } from "./style";

const FinalShortlistedPage = () => {
  const [candidates, setCandidates] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(5);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [roles, setRoles] = useState([]);

  const activeFilterCount =
    (roleFilter !== "" ? 1 : 0) +
    (statusFilter !== "" ? 1 : 0);

  const handleResetFilters = () => {
    setRoleFilter("");
    setStatusFilter("");
    setPage(1);
  };

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [formData, setFormData] = useState({
    currentCTC: "",
    expectedCTC: "",
    budget: "",
  });
  const [saving, setSaving] = useState(false);

  const fetchCandidates = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${FinalShortlistedEndPoint.GET_ALL}?page=${page}&limit=${limit}`;
      if (roleFilter) url += `&role=${encodeURIComponent(roleFilter)}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      const res = await axiosInstance.get(url);
      setCandidates(res.data.data || []);
      setTotal(res.data.total || 0);
      if (res.data.roles) setRoles(res.data.roles);
    } catch (err) {
      console.error("Failed to fetch final shortlisted", err);
    } finally {
      setLoading(false);
    }
  }, [page, limit, roleFilter, statusFilter]);

  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  const totalPages = Math.ceil(total / limit);

  const formatCTC = (value) => {
    if (value == null) return "—";
    return `₹${Number(value).toLocaleString()}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  const getMargin = (budget, expectedCTC) => {
    if (budget == null || expectedCTC == null) return null;
    return budget - expectedCTC;
  };

  const getMarginStatus = (margin) => {
    if (margin == null) return { label: "—", color: "#919eab", icon: null };
    if (margin > 0)
      return {
        label: "Within Budget",
        color: "#1e7e34",
        bg: "#e6f4ea",
        border: "#a3d9a5",
        icon: <CheckCircleIcon sx={{ fontSize: 16 }} />,
      };
    if (margin === 0)
      return {
        label: "At Budget",
        color: "#b8860b",
        bg: "#fef7e0",
        border: "#f0d58c",
        icon: <WarningIcon sx={{ fontSize: 16 }} />,
      };
    return {
      label: "Over Budget",
      color: "#d32f2f",
      bg: "#fce8e8",
      border: "#f5a5a5",
      icon: <ErrorIcon sx={{ fontSize: 16 }} />,
    };
  };

  const [originalFormData, setOriginalFormData] = useState({
    currentCTC: "",
    expectedCTC: "",
    budget: "",
  });

  const handleOpenModal = (candidate) => {
    setSelectedCandidate(candidate);
    const initial = {
      currentCTC: candidate.currentCTC ?? "",
      expectedCTC: candidate.expectedCTC ?? "",
      budget: candidate.budget ?? "",
    };
    setFormData(initial);
    setOriginalFormData(initial);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedCandidate(null);
  };

  const handleFormChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleUpdate = async () => {
    if (!selectedCandidate) return;
    setSaving(true);
    try {
      await axiosInstance.put(
        `${FinalShortlistedEndPoint.UPDATE}/${selectedCandidate._id}`,
        {
          expectedCTC:
            formData.expectedCTC !== "" ? Number(formData.expectedCTC) : null,
          budget:
            formData.budget !== "" ? Number(formData.budget) : null,
        },
      );
      handleCloseModal();
      fetchCandidates();
    } catch (err) {
      console.error("Failed to update compensation", err);
    } finally {
      setSaving(false);
    }
  };

  const format = (value) => {
    return value != null ? `${Number(value)} LPA` : "—";
  };

  const hasChanges =
    String(formData.expectedCTC) !== String(originalFormData.expectedCTC) ||
    String(formData.budget) !== String(originalFormData.budget);

  const modalBudget = formData.budget !== "" ? Number(formData.budget) : null;
  const modalExpected =
    formData.expectedCTC !== "" ? Number(formData.expectedCTC) : null;
  const modalMargin = getMargin(modalBudget, modalExpected);
  const modalMarginStatus = getMarginStatus(modalMargin);

  const SkeletonRow = () => (
    <TableRow>
      {Array.from({ length: 9 }).map((_, i) => (
        <TableCell key={i}>
          <Skeleton variant="text" width={i === 0 ? 150 : 80} height={24} />
        </TableCell>
      ))}
    </TableRow>
  );

  return (
    <FinalShortlistedWrapper>
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: "16px",
          backgroundColor: "#ffffff",
          border: "1px solid #e2e8f0",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.03)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            pb: 2,
            mb: 2,
            borderBottom: "1px solid #f1f5f9",
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "#1e293b",
                  letterSpacing: "-0.5px",
                  fontSize: "1.35rem",
                }}
              >
                Final Candidate List
              </Typography>
              <Chip
                label={`${total} Candidates`}
                size="small"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  backgroundColor: colors.primaryLight,
                  color: colors.primary,
                  borderRadius: "6px",
                }}
              />
            </Box>
            <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
              Aggregated view of top submissions for final review and compensation assessment.
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1.5,
              flex: 1,
              minWidth: { xs: "100%", md: "auto" },
            }}
          >
            <FormControl size="small" sx={{ minWidth: "210px" }}>
              <Select
                value={roleFilter}
                onChange={(e) => {
                  setRoleFilter(e.target.value);
                  setPage(1);
                }}
                displayEmpty
                startAdornment={
                  <InputAdornment position="start">
                    <WorkOutlineIcon sx={{ color: "#64748b", fontSize: "1.05rem" }} />
                  </InputAdornment>
                }
                sx={{
                  height: "38px",
                  borderRadius: "10px",
                  fontSize: "0.85rem",
                  fontWeight: 500,
                  backgroundColor: roleFilter !== "" ? colors.primaryLight : "#ffffff",
                  borderColor: roleFilter !== "" ? colors.primaryLightBorder : "#cbd5e1",
                  color: roleFilter !== "" ? colors.primary : "#334155",
                  "& .MuiSelect-select": { py: "6px", pl: 0.5 },
                }}
              >
                <MenuItem value="">All Roles</MenuItem>
                {roles.map((r) => (
                  <MenuItem key={r} value={r}>
                    {r}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: "190px" }}>
              <Select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                displayEmpty
                startAdornment={
                  <InputAdornment position="start">
                    <AccountBalanceWalletOutlinedIcon sx={{ color: "#64748b", fontSize: "1.05rem" }} />
                  </InputAdornment>
                }
                sx={{
                  height: "38px",
                  borderRadius: "10px",
                  fontSize: "0.85rem",
                  fontWeight: 500,
                  backgroundColor: statusFilter !== "" ? colors.primaryLight : "#ffffff",
                  borderColor: statusFilter !== "" ? colors.primaryLightBorder : "#cbd5e1",
                  color: statusFilter !== "" ? colors.primary : "#334155",
                  "& .MuiSelect-select": { py: "6px", pl: 0.5 },
                }}
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="withinBudget">Within Budget</MenuItem>
                <MenuItem value="atBudget">At Budget</MenuItem>
                <MenuItem value="overBudget">Over Budget</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {activeFilterCount > 0 && (
            <Button
              size="small"
              onClick={handleResetFilters}
              startIcon={<RotateLeftIcon sx={{ fontSize: "1rem" }} />}
              sx={{
                textTransform: "none",
                fontSize: "0.8rem",
                fontWeight: 600,
                color: colors.primary,
                backgroundColor: colors.primaryLight,
                border: `1px solid ${colors.primaryLightBorder}`,
                borderRadius: "8px",
                px: 1.5,
                py: "6px",
                height: "38px",
                whiteSpace: "nowrap",
                "&:hover": {
                  backgroundColor: colors.sidebarSelected || colors.primaryLight,
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                },
              }}
            >
              Clear {activeFilterCount} {activeFilterCount === 1 ? "Filter" : "Filters"}
            </Button>
          )}
        </Box>
      </Paper>

      <Box className="table-card">
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>CANDIDATE NAME</TableCell>
                <TableCell>ROLE</TableCell>
                <TableCell>RECRUITER NAME</TableCell>
                <TableCell>CURRENT CTC</TableCell>
                <TableCell>EXPECTED CTC</TableCell>
                <TableCell>OFFERED CTC</TableCell>
                <TableCell>YOE</TableCell>
                <TableCell>MARGIN (%)</TableCell>
                <TableCell>STATUS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: limit }).map((_, i) => (
                  <SkeletonRow key={i} />
                ))
              ) : candidates.length > 0 ? (
                candidates.map((c) => {
                  const margin = getMargin(c.budget, c.expectedCTC);
                  const status = getMarginStatus(margin);
                  return (
                    <TableRow
                      key={c._id}
                      sx={{ cursor: "pointer" }}
                      onClick={() => handleOpenModal(c)}
                    >
                      <TableCell>
                        <Typography className="candidate-name">
                          {c.candidateName}
                        </Typography>
                        <Typography className="candidate-sub">
                          Submitted: {formatDate(c.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>{c.role || "—"}</TableCell>
                      <TableCell>
                        <Typography className="recruiter-name">
                          {c.recruiterName || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>{format(c.currentCTC)}</TableCell>
                      <TableCell>{format(c.expectedCTC)}</TableCell>
                      <TableCell>{format(c.budget)}</TableCell>
                      <TableCell>
                        {c.totalExp != null ? c.totalExp : "—"}
                      </TableCell>
                      <TableCell>
                        {margin != null && c.budget ? (
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: "14px",
                              color:
                                margin > 0
                                  ? "#1e7e34"
                                  : margin === 0
                                  ? "#b8860b"
                                  : "#d32f2f",
                            }}
                          >
                            {((margin / c.budget) * 100).toFixed(1)}%
                          </Typography>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell>
                        {status.icon ? (
                          <Chip
                            icon={status.icon}
                            label={status.label}
                            size="small"
                            sx={{
                              backgroundColor: status.bg,
                              color: status.color,
                              border: `1px solid ${status.border}`,
                              fontWeight: 600,
                              fontSize: "12px",
                              height: "26px",
                              "& .MuiChip-icon": {
                                color: `${status.color} !important`,
                              },
                            }}
                          />
                        ) : (
                          "—"
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
                      <PersonOffIcon sx={{ fontSize: 56, color: "#d1d5db" }} />
                      <Typography variant="h6" fontWeight={600} color="text.secondary">
                        No shortlisted candidates found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Try adjusting your filters or check back later.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box className="pagination-bar">
        <Typography className="pagination-info">
          Showing {candidates.length > 0 ? (page - 1) * limit + 1 : 0} to{" "}
          {Math.min(page * limit, total)} of {total} entries
        </Typography>
        <Box className="pagination-buttons">
          <Button
            variant="contained"
            className="pagination-btn"
            onClick={() => setPage((p) => p - 1)}
            disabled={page === 1 || loading}
            startIcon={<NavigateBeforeIcon />}
          >
            Previous
          </Button>
          <Button
            variant="contained"
            className="pagination-btn"
            onClick={() => setPage((p) => p + 1)}
            disabled={page >= totalPages || loading}
            endIcon={<NavigateNextIcon />}
          >
            Next
          </Button>
        </Box>
      </Box>

      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 60px rgba(127, 29, 29, 0.15)",
          },
        }}
      >
        {selectedCandidate && (
          <>
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                pb: 1,
              }}
            >
              <Typography
                sx={{ fontSize: "20px", fontWeight: 700, color: colors.primary }}
              >
                Candidate Profile
              </Typography>
              <IconButton onClick={handleCloseModal} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 2,
                  p: 2,
                  borderRadius: "12px",
                  backgroundColor: colors.primaryLight,
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    backgroundColor: "#e8eef5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: "18px",
                    color: colors.primary,
                  }}
                >
                  {selectedCandidate.candidateName
                    ?.split(" ")
                    .map((w) => w[0])
                    .join("")
                    .substring(0, 2)
                    .toUpperCase()}
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "16px",
                      color: "#1a1a1a",
                    }}
                  >
                    {selectedCandidate.candidateName}
                  </Typography>
                  <Typography sx={{ fontSize: "13px", color: "#637381" }}>
                    {selectedCandidate.role}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 700,
                  color: colors.primary,
                  mb: 2,
                }}
              >
                Compensation Details{" "}
                <Typography
                  component="span"
                  sx={{
                    fontWeight: 400,
                    color: "#919eab",
                    fontStyle: "italic",
                  }}
                >
                  (Admin Only)
                </Typography>
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography sx={{ fontSize: "14px", color: "#374151" }}>
                    Current CTC (₹ LPA)
                  </Typography>
                  <TextField
                    size="small"
                    value={
                      formData.currentCTC !== "" ? formData.currentCTC : "—"
                    }
                    InputProps={{ readOnly: true }}
                    sx={{
                      width: 160,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        backgroundColor: "#f9fafb",
                      },
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography sx={{ fontSize: "14px", color: "#374151" }}>
                    Expected CTC (₹ LPA)
                  </Typography>
                  <TextField
                    size="small"
                    type="number"
                    placeholder="Enter amount"
                    value={formData.expectedCTC}
                    onChange={handleFormChange("expectedCTC")}
                    sx={{
                      width: 160,
                      "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography sx={{ fontSize: "14px", color: "#374151" }}>
                    Budgeted CTC (₹ LPA)
                  </Typography>
                  <TextField
                    size="small"
                    type="number"
                    placeholder="Enter amount"
                    value={formData.budget}
                    onChange={handleFormChange("budget")}
                    sx={{
                      width: 160,
                      "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                    }}
                  />
                </Box>
              </Box>

              {modalMargin != null && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      p: 2,
                      borderRadius: "12px",
                      backgroundColor: modalMarginStatus.bg || "#f9fafb",
                      border: `1px solid ${
                        modalMarginStatus.border || "#e8ecf0"
                      }`,
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "14px",
                        color: "#374151",
                      }}
                    >
                      Org Margin
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "16px",
                          color: modalMarginStatus.color,
                        }}
                      >
                        {modalMargin >= 0 ? "" : "-"}₹{" "}
                        {Math.abs(modalMargin).toLocaleString()} LPA
                        {modalBudget
                          ? ` (${((modalMargin / modalBudget) * 100).toFixed(
                              1,
                            )}%)`
                          : ""}
                      </Typography>
                      <Chip
                        icon={modalMarginStatus.icon}
                        label={modalMarginStatus.label}
                        size="small"
                        sx={{
                          backgroundColor: modalMarginStatus.bg,
                          color: modalMarginStatus.color,
                          border: `1px solid ${modalMarginStatus.border}`,
                          fontWeight: 600,
                          fontSize: "11px",
                          height: "24px",
                          "& .MuiChip-icon": {
                            color: `${modalMarginStatus.color} !important`,
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
              <Button
                variant="outlined"
                onClick={handleCloseModal}
                sx={{
                  textTransform: "none",
                  borderRadius: "10px",
                  padding: "8px 24px",
                  fontWeight: 600,
                  color: "#374151",
                  borderColor: "#d0d7df",
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleUpdate}
                disabled={saving || !hasChanges}
                startIcon={
                  saving ? (
                    <CircularProgress size={16} sx={{ color: "#fff" }} />
                  ) : null
                }
                sx={{
                  textTransform: "none",
                  borderRadius: "10px",
                  padding: "8px 24px",
                  fontWeight: 600,
                  backgroundColor: colors.primary,
                  "&:hover": { backgroundColor: colors.primaryHover },
                }}
              >
                Update
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </FinalShortlistedWrapper>
  );
};

export default FinalShortlistedPage;
